import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import debounce from "lodash/debounce";
import { markRaw, onMounted, ref, watch } from "vue";
import { IPhieuNhapKhoResponseBase } from "@/core/interfaces/ApiResponses";
import { PhieuNhapKhoService } from "@/core/services/PhieuNhapKho.service";
import {
  swalConfirm,
  swalErrNotification,
  swalNotification,
} from "@/core/helpers/utils";
import { usePagination } from "@/core/hooks/use-pagination";
import { useLoading } from "@/core/hooks/use-loading";
import { usePageHeader } from "@/core/hooks/usePageHeader";
import usePermission from "@/core/hooks/usePermission";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { ButtonsType } from "@/core/enums/buttons.enums";
import { openPhieuNhapKhoPdf } from "@/components/shared/pdf-viewer/open-phieu-nhap-kho-pdf";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import Pagination from "@/components/shared/pagination/Pagination.vue";
import PhieuNhapKhoTableV2 from "@/views/crafted/pages/phieu-nhap-kho/phieu-nhap-kho-table/PhieuNhapKhoTableV2.vue";
import ConfirmHoanThanh from "@/components/shared/confirm-hoan-thanh-modal/ConfirmHoanThanh.vue";
import IconCircleCheckClassicRegular from "@/icons/IconCircleCheckClassicRegular.vue";
import IconEye from "@/icons/IconEye.vue";
import IconMemoCircleCheckClassicSolid from "@/icons/IconMemoCircleCheckClassicSolid.vue";
import IconTrash from "@/icons/IconTrash.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'List',
  setup(__props) {

document.title = "Phê duyệt phiếu nhập kho - Đường sắt Hà Nội";

const { currentPage, totalItems, perPage } = usePagination();
const { startLoading, endLoading } = useLoading();
const items = ref<IPhieuNhapKhoResponseBase[]>([]);
const { canDuyetPnk } = usePermission();
const router = useRouter();

const selectedPhieuNhapKho = ref<IPhieuNhapKhoResponseBase | null>(null);
const isApprovalFlow = ref(false);

const { searchQuery } = usePageHeader({
  title: "Phê duyệt phiếu nhập kho",
  breadcrumbs: [{ text: "Phê duyệt" }, { text: "Phiếu nhập kho" }],
  showSearch: true,
  searchPlaceholder: "Số phiếu hoặc người giao ...",
});

const actionButtons = [
  {
    buttonType: ButtonsType.INFO,
    icon: markRaw(IconEye),
    label: "Xem chi tiết và in phiếu",
    tooltipMessage: "Xem chi tiết và in phiếu",
    condition: () => true,
    onClick: (row: IPhieuNhapKhoResponseBase) => onInfoHandler(row),
  },
  {
    buttonType: ButtonsType.TRASH,
    icon: markRaw(IconTrash),
    label: "Xóa phiếu nhập kho",
    tooltipMessage: "Xóa phiếu nhập kho",
    condition: (row: IPhieuNhapKhoResponseBase) =>
      !row.is_approved && !row.ngay_hoan_thanh,
    onClick: (row: IPhieuNhapKhoResponseBase) => onDeleteHandler(row),
  },
  {
    buttonType: ButtonsType.CONFIRM,
    icon: markRaw(IconMemoCircleCheckClassicSolid),
    label: "Xác nhận nhập kho",
    tooltipMessage: "Xác nhận nhập kho",
    condition: (row: IPhieuNhapKhoResponseBase) => !row.is_approved,
    onClick: (row: IPhieuNhapKhoResponseBase) => onApproveHandler(row),
  },
  {
    buttonType: ButtonsType.APPROVE,
    icon: markRaw(IconCircleCheckClassicRegular),
    label: "Giám đốc ký duyệt",
    tooltipMessage: "Giám đốc ký duyệt",
    condition: (row: IPhieuNhapKhoResponseBase) => !row.is_admin_signed,
    onClick: (row: IPhieuNhapKhoResponseBase) =>
      handleAdminAssignButtonClick(row),
  },
];

const fetchPhieuNhapKhoItems = async () => {
  try {
    startLoading();

    const {
      data: {
        data: { data, total },
      },
    } = await PhieuNhapKhoService.getListForApproval({
      per_page: perPage.value,
      current_page: currentPage.value,
      search: searchQuery.value,
    });

    totalItems.value = total;
    items.value = data;
    endLoading();
  } catch (e) {
    endLoading();
    await swalNotification(
      "Có lỗi xảy ra khi lấy thông tin danh sách phiếu nhập kho",
      "error"
    );
  }
};

watch(
  () => currentPage.value,
  async () => {
    await fetchPhieuNhapKhoItems();
  }
);

watch(
  () => perPage.value,
  () => {
    if (currentPage.value === 1) {
      fetchPhieuNhapKhoItems();
      return;
    }
    currentPage.value = 1;
  }
);

watch(
  searchQuery,
  debounce(() => {
    fetchPhieuNhapKhoItems();
  }, 500)
);

const onInfoHandler = (phieuNhapKho: IPhieuNhapKhoResponseBase) => {
  openPhieuNhapKhoPdf(router, phieuNhapKho.id);
};

const onDeleteHandler = async (phieuNhapKho: IPhieuNhapKhoResponseBase) => {
  const confirmed = await swalConfirm({
    title: "Xác nhận xóa",
    text: `Bạn có chắc chắn muốn xóa phiếu nhập kho số ${phieuNhapKho.so_phieu}?`,
    icon: "warning",
    confirmButtonText: "Xóa",
    confirmButtonClass: "btn btn-danger",
  });

  if (!confirmed) return;

  try {
    startLoading();

    await PhieuNhapKhoService.delete(phieuNhapKho.id);

    endLoading();

    await swalNotification("Xóa phiếu nhập kho thành công", "success");
    await fetchPhieuNhapKhoItems();
  } catch (e) {
    endLoading();
    await swalNotification("Có lỗi xảy ra khi xóa phiếu nhập kho", "error");
  }
};

const onApproveHandler = (phieuNhapKho: IPhieuNhapKhoResponseBase) => {
  selectedPhieuNhapKho.value = phieuNhapKho;
  isApprovalFlow.value = true;
};

const { commit } = useStore();

const handleAdminAssignButtonClick = (
  phieuNhapKho: IPhieuNhapKhoResponseBase
) => {
  if (phieuNhapKho.is_admin_signed) return;

  commit("ConfirmationDialogModule/setDialog", {
    title: `Phiếu nhập số ${phieuNhapKho.so_phieu}`,
    message: "Xác nhận giám đốc ký duyệt phiếu nhập này?",
    onConfirm: async () => {
      commit("ConfirmationDialogModule/closeDialog");
      await startLoading();

      try {
        const {
          data: { message },
        } = await PhieuNhapKhoService.adminSign(phieuNhapKho.id, {
          is_admin_signed: true,
        });
        await endLoading();
        await swalNotification(
          message || "Giám đốc ký duyệt phiếu nhập thành công"
        );
        await fetchPhieuNhapKhoItems();
      } catch (e) {
        await endLoading();
        await swalErrNotification(e, "Có lỗi xảy ra");
      }
    },
  });
};

const confirmHoanThanh = async () => {
  selectedPhieuNhapKho.value = null;
  isApprovalFlow.value = false;
  await fetchPhieuNhapKhoItems();
};

const closeConfirmHoanThanh = () => {
  selectedPhieuNhapKho.value = null;
  isApprovalFlow.value = false;
};

onMounted(async () => {
  if (!canDuyetPnk.value) {
    await swalNotification(
      "Bạn không có quyền thực hiện chức năng này",
      "warning"
    );

    router.replace("/trang-chu");

    return;
  }

  await fetchPhieuNhapKhoItems();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ConfirmHoanThanh, {
      for: 'phieu_nhap',
      "phieu-nhap-data": selectedPhieuNhapKho.value,
      show: !!selectedPhieuNhapKho.value && isApprovalFlow.value,
      "is-approval-flow": isApprovalFlow.value,
      onClose: closeConfirmHoanThanh,
      onConfirm: confirmHoanThanh
    }, null, 8, ["phieu-nhap-data", "show", "is-approval-flow"]),
    _createVNode(PageContentBase, null, {
      default: _withCtx(() => [
        _createVNode(PhieuNhapKhoTableV2, {
          "action-buttons": actionButtons,
          "cur-page": _unref(currentPage),
          items: items.value,
          "per-page": _unref(perPage),
          "show-xac-nhan-button": false,
          "show-duyet-column": true,
          onRefresh: fetchPhieuNhapKhoItems
        }, null, 8, ["cur-page", "items", "per-page"]),
        (items.value.length > 0)
          ? (_openBlock(), _createBlock(Pagination, {
              key: 0,
              page: _unref(currentPage),
              "page-size": _unref(perPage),
              total: _unref(totalItems),
              onChangePage: _cache[0] || (_cache[0] = (value) => (currentPage.value = value)),
              onChangePageSize: _cache[1] || (_cache[1] = (value) => (perPage.value = value))
            }, null, 8, ["page", "page-size", "total"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 64))
}
}

})